import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiCheck, FiArrowRight, FiInfo, FiExternalLink } = FiIcons;

const buildIncludes = [
  'Current-state assessment',
  'JSM environment design and build',
  'Asset schema and request types',
  'Queues, SLAs, and portal setup',
  'Automation configuration',
  'Data migration from spreadsheets',
  'Team walkthrough and go-live support',
];

const tiers = [
  {
    name: 'Core',
    price: '$1,500',
    desc: 'Admin, minor config changes, and a monthly review call.',
    includes: [
      'Ongoing JSM admin support',
      'Minor configuration changes',
      'Monthly review call',
      'One active workstream at a time',
    ],
  },
  {
    name: 'Growth',
    price: '$2,500',
    desc: 'Everything in Core, plus new workflows, automations, and one major change per month.',
    includes: [
      'Everything in Core',
      'New workflow and automation builds',
      'One major change per month',
      'Queue and SLA updates',
      'Form and portal changes',
    ],
    highlight: true,
  },
  {
    name: 'Scale',
    price: '$4,000',
    desc: 'Everything in Growth, plus priority response and multiple active workstreams.',
    includes: [
      'Everything in Growth',
      'Priority response',
      'Multiple active workstreams',
      'Faster turnaround on all requests',
      'Deeper scope per cycle',
    ],
  },
];

const HomePricing = () => {
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <section id="pricing" className="py-24" style={{ background: '#f0fdf4' }}>
      <div className="max-w-4xl mx-auto px-8 lg:px-16">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-12">
          <p className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: '#16a34a' }}>Pricing</p>
          <h2 className="text-3xl md:text-4xl font-bold mb-3" style={{ color: '#052e16' }}>Fixed-fee build. Monthly retainer. No surprises.</h2>
          <p className="text-sm max-w-xl leading-relaxed" style={{ color: '#166534' }}>
            Two components: a fixed-fee implementation and a bounded monthly retainer. Both are confirmed during discovery before anything starts.
          </p>
        </motion.div>

        {/* Implementation card */}
        <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="p-8 rounded-xl border mb-6" style={{ background: '#052e16', borderColor: '#166534' }}>
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
            <div className="flex-1">
              <span className="text-xs font-bold px-2 py-1 rounded" style={{ background: 'rgba(74,222,128,0.15)', color: '#4ade80' }}>
                Fixed Fee · One-Time
              </span>
              <h3 className="text-xl font-bold text-white mt-3 mb-1">Implementation</h3>
              <p className="text-4xl font-bold text-white mb-1">
                $5,000 – <span style={{ color: '#4ade80' }}>$12,000</span>
              </p>
              <p className="text-sm mb-0" style={{ color: '#86efac' }}>
                Based on number of asset types, locations, and integration complexity.
              </p>
            </div>
            <div className="flex-1 space-y-2.5">
              {buildIncludes.map((item, i) => (
                <div key={i} className="flex items-start gap-2">
                  <SafeIcon icon={FiCheck} className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: '#4ade80' }} />
                  <span className="text-sm" style={{ color: '#bbf7d0' }}>{item}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Retainer tiers */}
        <div className="mb-6">
          <p className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: '#16a34a' }}>
            JSM Ops Retainer · Monthly
          </p>
          <div className="grid md:grid-cols-3 gap-4">
            {tiers.map((t, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className="p-6 rounded-xl border flex flex-col"
                style={{
                  background: t.highlight ? '#052e16' : '#ffffff',
                  borderColor: t.highlight ? '#166534' : '#bbf7d0',
                }}>
                <div className="mb-4">
                  {t.highlight && (
                    <span className="text-xs font-bold px-2 py-1 rounded inline-block mb-2"
                      style={{ background: 'rgba(74,222,128,0.15)', color: '#4ade80' }}>
                      Most Popular
                    </span>
                  )}
                  <h3 className="text-lg font-bold" style={{ color: t.highlight ? '#ffffff' : '#052e16' }}>{t.name}</h3>
                  <p className="text-3xl font-bold mt-1" style={{ color: t.highlight ? '#4ade80' : '#052e16' }}>
                    {t.price}<span className="text-base font-normal" style={{ color: t.highlight ? '#86efac' : '#166534' }}>/mo</span>
                  </p>
                  <p className="text-xs leading-relaxed mt-2" style={{ color: t.highlight ? '#86efac' : '#166534' }}>
                    {t.desc}
                  </p>
                </div>
                <div className="space-y-2 flex-1">
                  {t.includes.map((item, j) => (
                    <div key={j} className="flex items-start gap-2">
                      <div className="w-4 h-4 rounded flex items-center justify-center flex-shrink-0 mt-0.5"
                        style={{ background: t.highlight ? 'rgba(74,222,128,0.15)' : '#dcfce7' }}>
                        <SafeIcon icon={FiCheck} className="w-2.5 h-2.5" style={{ color: t.highlight ? '#4ade80' : '#16a34a' }} />
                      </div>
                      <span className="text-sm" style={{ color: t.highlight ? '#bbf7d0' : '#166534' }}>{item}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Atlassian licensing note */}
        <div className="p-5 rounded-xl border mb-4" style={{ background: '#ffffff', borderColor: '#bbf7d0' }}>
          <div className="flex items-start gap-3">
            <SafeIcon icon={FiExternalLink} className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#16a34a' }} />
            <p className="text-xs leading-relaxed" style={{ color: '#166534' }}>
              <strong style={{ color: '#052e16' }}>Jira Service Management licensing is billed directly through Atlassian and is not included in Valtivo's fees.</strong> We'll help you understand what license tier makes sense for your team size and use case — but the subscription is between you and Atlassian.
            </p>
          </div>
        </div>

        {/* AI Voice add-on callout */}
        <div className="p-5 rounded-xl border mb-4" style={{ background: '#ffffff', borderColor: '#bbf7d0' }}>
          <div className="flex items-start gap-3">
            <SafeIcon icon={FiInfo} className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#16a34a' }} />
            <p className="text-xs leading-relaxed" style={{ color: '#166534' }}>
              <strong style={{ color: '#052e16' }}>AI voice intake is a separate add-on — starting at $499/mo.</strong> No setup fee for existing JSM clients. Monthly cost scales with call volume. Scoped after the core build is confirmed.
            </p>
          </div>
        </div>

        <div className="flex items-start gap-3 p-5 rounded-xl border mb-8" style={{ background: '#ffffff', borderColor: '#bbf7d0' }}>
          <SafeIcon icon={FiInfo} className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#16a34a' }} />
          <p className="text-xs leading-relaxed" style={{ color: '#166534' }}>
            <strong style={{ color: '#052e16' }}>Exact pricing is confirmed during discovery.</strong> More complex environments — multiple sites, many asset types, integrations with existing tools — may land at the higher end. We are transparent about this before anything starts.
          </p>
        </div>

        <div className="text-center">
          <button onClick={() => scrollTo('booking')} className="group inline-flex items-center gap-2 px-8 py-4 rounded font-bold text-base" style={{ background: '#16a34a', color: '#ffffff' }}>
            Talk Through Your Setup
            <SafeIcon icon={FiArrowRight} className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default HomePricing;